import { ApplicationConfig, DEFAULT_CURRENCY_CODE, importProvidersFrom, isDevMode } from '@angular/core';
import {
  PreloadAllModules,
  provideRouter,
  RouteReuseStrategy,
  withInMemoryScrolling,
  withPreloading,
} from '@angular/router';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { createTranslateLoader } from '@fitup-monorepo/core/lib/util/translate-loader.util';
import { FormsModule } from '@angular/forms';
import { NgCircleProgressModule } from 'ng-circle-progress';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFireAuthModule } from '@angular/fire/compat/auth';
import { AngularFirestoreModule, SETTINGS as FIRESTORE_SETTINGS } from '@angular/fire/compat/firestore';
import { AngularFireMessagingModule } from '@angular/fire/compat/messaging';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideRouterStore } from '@ngrx/router-store';
import { environment } from '@fitup-monorepo/core/lib/environment';
import { metaReducers, reducers } from './state';
import { AuthInterceptor } from '@fitup-monorepo/core/lib/interceptors/auth.interceptor';
import { AuthExpiredInterceptor } from '@fitup-monorepo/core/lib/interceptors/auth-expired.interceptor';
import { AppVersionInterceptor } from '@fitup-monorepo/core/lib/interceptors/app-version.interceptor';
import { TimeZoneInterceptor } from '@fitup-monorepo/core/lib/interceptors/time-zone.interceptor';
import { HttpLoggingInterceptor } from '@fitup-monorepo/core/lib/interceptors/http-logging.interceptor';
import { FirestoreService } from '@fitup-monorepo/core/lib/services/firestore/firestore.service';
import { TranslatableTextService } from '@fitup-monorepo/core/lib/services/translatable-text/translatable-text.service';
import { ProfileEffects } from '@fitup-monorepo/core/lib/state/profile/profile.effects';
import { ChallengeEffects } from '@fitup-monorepo/challenge/lib/state/challenge.effects';
import { ErrorEffects } from './state/error/effects/error.effects';
import { ChallengeDetailEffects } from '@fitup-monorepo/challenge/lib/pages/challenge-detail/challenge-detail.effects';
import { BadgeEffects } from '@fitup-monorepo/badges/lib/state/badge.effects';
import { BadgesPageEffects } from '@fitup-monorepo/badges/lib/badges.page.effects';
import { MiniChallengesEffects } from '@fitup-monorepo/home-dashboard/lib/components/mini-challenges/state/mini-challenges.effects';
import { NotificationsEffects } from '@fitup-monorepo/core/lib/state/notifications/notifications.effects';
import { ClubEffects } from '@fitup-monorepo/challenge/lib/state/club/club.effects';
import { ClubFeedEffects } from '@fitup-monorepo/challenge/lib/state/club-feed/club-feed.effects';
import { ClubDetailEffects } from '@fitup-monorepo/challenge/lib/pages/club-detail/club-detail.effects';
import { ActivityEffects } from '@fitup-monorepo/activity/lib/state/activity.effects';
import { DailyTasksEffects } from '@fitup-monorepo/home-dashboard/lib/components/daily-tasks-overview/state/daily-tasks.effects';
import { RewardSystemEffects } from '@fitup-monorepo/reward-system/lib/state/reward-system.effects';
import { VirtualCoachEffects } from '@fitup-monorepo/virtual-coach/lib/state/virtual-coach.effects';
import { FitupOnboardingWizardService } from './service/fitup-onboarding-wizard.service';
import {
  APP,
  APP_UPDATE,
  BROWSER,
  BUGFENDER,
  CAMERA,
  DEVICE,
  FILE_PICKER,
  FILESYSTEM,
  HAPTICS,
  HEALTH,
  KEEP_AWAKE,
  LIVE_UPDATE,
  NATIVE_AUDIO,
  NATIVE_SETTINGS,
  PREFERENCES,
  PUSH_NOTIFICATIONS,
  SCREEN_ORIENTATION,
  SHARE,
  STATUS_BAR,
  SYSTEM_BARS_PLUGIN,
  WINDOW,
  WIZARD_SERVICE,
} from '@fitup-monorepo/core/lib/capacitor-injection-tokens';
import { ScreenOrientation } from '@capawesome/capacitor-screen-orientation';
import { FilePicker } from '@capawesome/capacitor-file-picker';
import { Camera } from '@capacitor/camera';
import { Haptics } from '@capacitor/haptics';
import { Share } from '@capacitor/share';
import { LiveUpdate } from '@capawesome/capacitor-live-update';
import { AppUpdate } from '@capawesome/capacitor-app-update';
import { App } from '@capacitor/app';
import { PushNotifications } from '@capacitor/push-notifications';
import { Preferences } from '@capacitor/preferences';
import { Health } from 'capacitor-health';
import { NativeSettings } from 'capacitor-native-settings';
import { Device } from '@capacitor/device';
import { KeepAwake } from '@capacitor-community/keep-awake';
import { StatusBar } from '@capacitor/status-bar';
import { Browser } from '@capacitor/browser';
import { NativeAudio } from '@capgo/native-audio';
import { Filesystem } from '@capacitor/filesystem';
import { Bugfender } from '@bugfender/capacitor';
import { SystemBars } from '@capacitor/core';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { routes } from './app.routes';
import { provideMarkdown } from 'ngx-markdown';
import { WaterIntakeEffects } from '@fitup-monorepo/components/lib/water-tracking/state/water-intake.effects';

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      withPreloading(PreloadAllModules),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      })
    ),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    provideMarkdown(),
    importProvidersFrom(
      IonicModule.forRoot(),
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient],
        },
      }),
      FormsModule,
      NgCircleProgressModule.forRoot(),
      ServiceWorkerModule.register('firebase-messaging-sw.js', {
        enabled: environment.production,
      }),
      AngularFireModule.initializeApp(environment.firebase),
      AngularFireAuthModule,
      AngularFirestoreModule,
      AngularFireMessagingModule,
      ServiceWorkerModule.register('ngsw-worker.js', {
        enabled: environment.production,
        registrationStrategy: 'registerWhenStable:30000',
      })
    ),
    provideStore(reducers, { metaReducers }),
    provideEffects(
      ProfileEffects,
      ChallengeEffects,
      ErrorEffects,
      ChallengeDetailEffects,
      BadgeEffects,
      BadgesPageEffects,
      MiniChallengesEffects,
      NotificationsEffects,
      WaterIntakeEffects,
      ClubEffects,
      ClubFeedEffects,
      ClubDetailEffects,
      ActivityEffects,
      DailyTasksEffects,
      RewardSystemEffects,
      VirtualCoachEffects
    ),
    provideStoreDevtools({ maxAge: 25, logOnly: !isDevMode(), autoPause: true }),
    provideRouterStore(),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    {
      provide: FIRESTORE_SETTINGS,
      useValue: {
        experimentalAutoDetectLongPolling: true,
      },
    },
    {
      provide: RouteReuseStrategy,
      useClass: IonicRouteStrategy,
    },
    {
      // first, so it is the outermost interceptor and its duration covers the whole chain
      provide: HTTP_INTERCEPTORS,
      useClass: HttpLoggingInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthExpiredInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AppVersionInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TimeZoneInterceptor,
      multi: true,
    },
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
    { provide: SCREEN_ORIENTATION, useValue: ScreenOrientation },
    { provide: FILE_PICKER, useValue: FilePicker },
    { provide: CAMERA, useValue: Camera },
    { provide: HAPTICS, useValue: Haptics },
    { provide: SHARE, useValue: Share },
    { provide: LIVE_UPDATE, useValue: LiveUpdate },
    { provide: WIZARD_SERVICE, useClass: FitupOnboardingWizardService },
    { provide: APP_UPDATE, useValue: AppUpdate },
    { provide: APP, useValue: App },
    { provide: PUSH_NOTIFICATIONS, useValue: PushNotifications },
    { provide: PREFERENCES, useValue: Preferences },
    { provide: HEALTH, useValue: Health },
    { provide: NATIVE_SETTINGS, useValue: NativeSettings },
    { provide: DEVICE, useValue: Device },
    { provide: KEEP_AWAKE, useValue: KeepAwake },
    { provide: STATUS_BAR, useValue: StatusBar },
    { provide: WINDOW, useValue: window },
    { provide: BROWSER, useValue: Browser },
    { provide: NATIVE_AUDIO, useValue: NativeAudio },
    { provide: FILESYSTEM, useValue: Filesystem },
    { provide: BUGFENDER, useValue: Bugfender },
    { provide: SYSTEM_BARS_PLUGIN, useValue: SystemBars },
    FirestoreService,
    TranslatableTextService,
  ],
};
